/**
 * @fileoverview Outreach Vuex mutations for managing state.
 * @module store/modules/outreach/mutations
 */

import { MutationTree } from "vuex";
import { OutreachState } from "./types";

const mutations: MutationTree<OutreachState> = {
  /**
   * Sets outreach contacts array and count.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Payload object
   * @param {Array} [payload.data=[]] - Array of contact objects
   * @param {number} [payload.count=0] - Total count of contacts
   */
  SET_OUTREACH_CONTACTS(state, payload) {
    const { count = 0, data = [] } = payload;
    state.contacts.data = data;
    state.contacts.count = count;
  },
  /**
   * Sets the outreach tags array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Array} payload - Array of tag objects
   */
  SET_OUTREACH_TAGS(state, payload) {
    state.tags.data = payload;
  },
  /**
   * Adds a new tag to the outreach tags array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Tag object to add
   */
  ADD_OUTREACH_TAG(state, payload) {
    state.tags.data = [...state.tags.data, payload];
  },
  /**
   * Removes a tag from the outreach tags array by ID.
   *
   * @param {OutreachState} state - Current module state
   * @param {string} payload - Tag ID to remove
   */
  REMOVE_OUTREACH_TAG(state, payload) {
    state.tags.data = [...state.tags.data].filter((tag) => tag.id !== payload);
  },
  /**
   * Updates an existing tag in the outreach tags array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Tag object with updated properties
   * @param {string} payload.id - Tag ID to update
   */
  UPDATE_OUTREACH_TAG(state, payload) {
    state.tags.data = [...state.tags.data].map((tag) => {
      if (tag.id === payload.id) return { ...tag, ...payload };
      return tag;
    });
  },
  /**
   * Adds a new stage to the outreach stages array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Stage object to add
   */
  ADD_OUTREACH_STAGE(state, payload) {
    state.stages.data = [...state.stages.data, payload];
  },
  /**
   * Removes a stage from the outreach stages array by ID.
   *
   * @param {OutreachState} state - Current module state
   * @param {string} payload - Stage ID to remove
   */
  REMOVE_OUTREACH_STAGE(state, payload) {
    state.stages.data = [...state.stages.data].filter((tag) => tag.id !== payload);
  },
  /**
   * Updates an existing stage in the outreach stages array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Stage object with updated properties
   * @param {string} payload.id - Stage ID to update
   */
  UPDATE_OUTREACH_STAGE(state, payload) {
    state.stages.data = [...state.stages.data].map((tag) => {
      if (tag.id === payload.id) return { ...tag, ...payload };
      return tag;
    });
  },
  /**
   * Sets the outreach stages array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Array} payload - Array of stage objects
   */
  SET_OUTREACH_STAGES(state, payload) {
    state.stages.data = payload;
  },
  /**
   * Sets the loading state for outreach operations.
   *
   * @param {OutreachState} state - Current module state
   * @param {boolean} payload - Loading state value
   */
  SET_OUTREACH_LOADING(state, payload) {
    state.isLoading = payload;
  },
  /**
   * Sets the contact filter board configuration.
   *
   * @param {OutreachState} state - Current module state
   * @param {Array} payload - Filter board configuration
   */
  SET_CONTACT_FILTER_BOARD(state, payload) {
    state.filterBoard = payload;
  },
  /**
   * Sets the contact sort board configuration.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Sort board configuration
   */
  SET_CONTACT_SORT_BOARD(state, payload) {
    state.sortBoard = payload;
  },
  /**
   * Sets the contact attributes array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Array} payload - Array of contact attribute definitions
   */
  SET_CONTACT_ATTRIBUTES(state, payload) {
    state.contactAttributes = payload;
  },
  /**
   * Sets a single outreach metric.
   *
   * @param {OutreachState} state - Current module state
   * @param {Object} payload - Metric object
   * @param {string} payload.name - Metric name (used as key)
   */
  SET_OUTREACH_METRIC(state, payload) {
    state.metrics = {
      ...state.metrics,
      [payload.name]: payload,
    };
  },
  /**
   * Sets all outreach metrics from an array.
   *
   * @param {OutreachState} state - Current module state
   * @param {Array} payload - Array of metric objects
   */
  SET_OUTREACH_METRICS(state, payload) {
    state.metrics = payload.reduce((acc, cur, i) => {
      acc[i] = cur;
      return acc;
    }, {});
  },
  /**
   * Removes an outreach metric by ID.
   *
   * @param {OutreachState} state - Current module state
   * @param {number} metricId - Metric ID to remove
   */
  REMOVE_OUTREACH_METRIC(state, metricId) {
    state.metrics = Object.keys(state.metrics).filter(key => key !== String(metricId)).reduce((acc, cur) => {
      acc[cur] =state.metrics[cur];
      return acc;
    }, {});
  }
};
export default mutations;
